import {
  ModIconButton,
  ModTag,
  ModTooltip
} from '@moderneinc/design-system-components';
import {
  borderRadius,
  iconSize,
  semanticColors,
  spacing,
  typography
} from '@moderneinc/design-system-tokens';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import {
  Fragment,
  type FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { isUserOrgGroupingNode } from '../../helpers/organizations.helper';
import { splitOnSearchTerms } from '../../helpers/search-highlight.helpers';
import { pluralize } from '../../helpers/string.helper';
import { useI18n } from '../../hooks/use-i18n.hooks';
import {
  CheckmarkIcon,
  CollapseIcon,
  CopyIcon,
  ExpandIcon
} from '../../icons/icons';
import { CopyToClipboardWithTooltip } from '../utilities/copy-pasta/copy-to-clipboard-with-tooltip.component';
import { LocaleNumber } from '../utilities/locale-number/locale-number.component';
import { MiddleTruncatedLabel } from '../utilities/middle-truncated-label/middle-truncated-label.component';
import {
  buildPrunedTree,
  type OrganizationTreeItem,
  type OrgRowActionTarget,
  type PrunedTreeRow,
  type SearchHit
} from './organizations.rows';

const isBucketRow = (id: string): boolean => isUserOrgGroupingNode(id);

const ROW_ACTIONS_CLASSNAME = 'org-row-actions';

// Inset: an outer ring is clipped by the scroller and covered by the next row.
export const TREE_ROW_FOCUS_RING_SX = {
  boxShadow: `inset 0 0 0 2px ${semanticColors.color.border.focus}`,
  borderRadius: `${borderRadius.s}px`
} as const;

/** Absolute-positions a row at its virtualizer-assigned offset. */
const virtualRowStyle = (
  start: number
): {
  position: 'absolute';
  top: number;
  left: number;
  width: string;
  transform: string;
} => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  transform: `translateY(${start}px)`
});

type RowHoverWrapperProps = {
  elementId: string;
  tabIndex: number;
  ariaSelected: boolean;
  ariaExpanded?: boolean;
  onContextMenu: ((event: React.MouseEvent) => void) | undefined;
  isHit: boolean;
  virtualRow: VirtualItem;
  measureRef: (el: Element | null) => void;
  children: React.ReactNode;
};

const RowHoverWrapper: FunctionComponent<RowHoverWrapperProps> = ({
  elementId,
  tabIndex,
  ariaSelected,
  ariaExpanded,
  onContextMenu,
  isHit,
  virtualRow,
  measureRef,
  children
}) => (
  <Box
    id={elementId}
    role="treeitem"
    tabIndex={tabIndex}
    aria-selected={ariaSelected}
    aria-expanded={ariaExpanded}
    onContextMenu={onContextMenu}
    data-index={virtualRow.index}
    ref={measureRef}
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      minHeight: 36,
      borderBottom: ROW_DIVIDER_BORDER,
      backgroundColor: isHit
        ? semanticColors.button.secondary.bgHover
        : undefined,
      // Focus stays in the search input; ring the Enter target instead.
      ...(ariaSelected && TREE_ROW_FOCUS_RING_SX),
      ...virtualRowStyle(virtualRow.start),
      '&:hover': {
        backgroundColor: semanticColors.button.secondary.bgHover
      },
      [`&:hover .${ROW_ACTIONS_CLASSNAME}, &:focus-within .${ROW_ACTIONS_CLASSNAME}`]:
        { visibility: 'visible' }
    }}
  >
    {children}
  </Box>
);

const BoldedMatch: FunctionComponent<{
  text: string;
  term: string;
}> = ({ text, term }) => {
  return (
    <span>
      {splitOnSearchTerms(text, [term]).map((segment, index) =>
        segment.highlighted ? (
          <Box
            key={index}
            component="mark"
            sx={{
              fontWeight: typography.fontWeight.extraBold,
              backgroundColor: 'transparent',
              color: 'inherit'
            }}
          >
            {segment.text}
          </Box>
        ) : (
          <Fragment key={index}>{segment.text}</Fragment>
        )
      )}
    </span>
  );
};

const RowCopyAction: FunctionComponent<{
  label: string;
}> = ({ label }) => (
  <Stack
    direction="row"
    className={ROW_ACTIONS_CLASSNAME}
    onClick={(event) => event.stopPropagation()}
    sx={{
      display: 'flex',
      visibility: 'hidden',
      flexShrink: 0,
      alignItems: 'center',
      ml: 'auto'
    }}
  >
    <CopyToClipboardWithTooltip text={label} tooltip="Copy to clipboard">
      {(copied) => (
        <span style={{ display: 'flex' }}>
          <ModIconButton
            size="small"
            component="span"
            aria-label={`Copy ${label} to clipboard`}
            sx={{
              '&:hover': {
                backgroundColor: semanticColors.button.secondary.bgHover
              }
            }}
          >
            {copied ? (
              <CheckmarkIcon
                size={iconSize.sm}
                color={semanticColors.color.status.success.solid}
              />
            ) : (
              <CopyIcon size={iconSize.sm} />
            )}
          </ModIconButton>
        </span>
      )}
    </CopyToClipboardWithTooltip>
  </Stack>
);

const ICON_BUTTON_SMALL_SIZE = 32;
const INDENT_PX = ICON_BUTTON_SMALL_SIZE + 4;
const BASE_PL = 8;
/**
 * Offset applied to "+ N more" / "Show more" rows so their text aligns
 * with the label column of the regular folder/match rows at the same
 * depth (which prepend a 32px chevron-or-placeholder + 12px gap).
 */
const HINT_LABEL_OFFSET = ICON_BUTTON_SMALL_SIZE + 12;

/** Shared sx for every row in the tree (folder, match, more, showMore). */
const ROW_BASE_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  width: '100%',
  textAlign: 'left',
  py: 0.5,
  pr: 1,
  minHeight: 36
} as const;

const ROW_DIVIDER_BORDER = `1px solid ${semanticColors.color.border.primary}`;

/** Shared sx for the secondary "+ N more" / "Show more" hint rows. */
const HINT_ROW_SX = {
  ...ROW_BASE_SX,
  borderBottom: ROW_DIVIDER_BORDER,
  fontSize: typography.fontSize.xs,
  color: semanticColors.color.text.secondary,
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    color: semanticColors.color.text.primary
  }
} as const;

type HintRowProps = {
  index: number;
  start: number;
  indent: number;
  label: string;
  ariaLabel: string;
  onClick: () => void;
  disabled: boolean;
  hasFailed?: boolean;
  measureRef: (el: Element | null) => void;
};

const HintRow: FunctionComponent<HintRowProps> = ({
  index,
  start,
  indent,
  label,
  ariaLabel,
  onClick,
  disabled,
  hasFailed = false,
  measureRef
}) => (
  <div data-index={index} ref={measureRef} style={virtualRowStyle(start)}>
    <ButtonBase
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      sx={{
        ...HINT_ROW_SX,
        pl: `${indent + HINT_LABEL_OFFSET}px`,
        ...(hasFailed && { color: semanticColors.color.status.error.solid })
      }}
    >
      {label}
    </ButtonBase>
  </div>
);

const rowElementId = (key: string): string => `org-filtered-tree-row-${key}`;
const matchRowElementId = (matchIndex: number): string =>
  `org-filtered-tree-match-${matchIndex}`;

/**
 * Children that match a hit are excluded — those are already in the spine.
 * Order is the API's response order. Grows over multiple "Show more" fetches.
 *
 * The discriminated `hasMore` half ensures `cursor` is required iff there's
 * actually a next page — `{ hasMore: true, cursor: undefined }` is
 * unrepresentable.
 */
type RevealedFolderState = {
  children: OrganizationTreeItem[];
} & ({ hasMore: false } | { hasMore: true; cursor: string });

type OrgFilteredTreeProps = {
  /**
   * Sparse tree containing matches and their ancestors only. Built by
   * `buildSpineFromHits` from the server-side search response.
   */
  spineItems: OrganizationTreeItem[];
  searchTerm: string;
  searchHits: SearchHit[];
  activeMatchIndex: number;
  /** Matches beyond the loaded pages; absent once all are in. */
  moreMatchCount?: number;
  isLoadingMoreMatches: boolean;
  onLoadMoreMatches: () => void;
  /**
   * Optional pre-fetched counts of non-matching siblings per folder ID.
   * When present, the "Show more" hint is rendered as "+ N more" with the
   * count; otherwise it shows generically and the count appears once
   * children are loaded.
   */
  hiddenSiblingCountByFolder?: Map<string, number>;
  /** Children fetched on demand for folders the user has expanded. */
  revealedChildren: Map<string, RevealedFolderState>;
  /** Folders with an in-flight children fetch. */
  loadingFolders: Set<string>;
  /** Folders whose most recent fetch threw — drives a retry affordance. */
  failedFolders: Set<string>;
  /**
   * Request the FIRST page of children for a folder. Result is appended
   * to `revealedChildren`.
   */
  onLoadChildren: (folderId: string) => void;
  /**
   * Request the NEXT page of children for an already-revealed folder.
   * Result is appended to `revealedChildren[folderId].children`.
   */
  onLoadMoreChildren: (folderId: string) => void;
  onSelect: (itemId: string) => void;
  onMatchClick: (matchIndex: number) => void;
  onOpenRowContextMenu: (
    event: React.MouseEvent,
    target: OrgRowActionTarget
  ) => void;
};

export const OrgFilteredTree: FunctionComponent<OrgFilteredTreeProps> = ({
  spineItems,
  searchTerm,
  searchHits,
  activeMatchIndex,
  moreMatchCount,
  isLoadingMoreMatches,
  onLoadMoreMatches,
  hiddenSiblingCountByFolder,
  revealedChildren,
  loadingFolders,
  failedFolders,
  onLoadChildren,
  onLoadMoreChildren,
  onSelect,
  onMatchClick,
  onOpenRowContextMenu
}) => {
  const { formatNumber } = useI18n();
  const containerRef = useRef<HTMLDivElement>(null);

  // Folders the user has expanded (either via "+N more" or by clicking a
  // revealed folder's chevron). Distinct from `revealedChildren` because the
  // former is UI state, the latter is fetched data.
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(
    () => new Set()
  );
  // Spine folders start expanded, so track collapses.
  const [collapsedSpineFolders, setCollapsedSpineFolders] = useState<
    Set<string>
  >(() => new Set());

  const basePrunedRows = useMemo(
    () => buildPrunedTree({ items: spineItems, searchHits }),
    [spineItems, searchHits]
  );

  // Compose the full visible-row list: spine rows interleaved with revealed
  // children for any folders the user has expanded.
  const visibleRows = useMemo(() => {
    const result: Array<
      | PrunedTreeRow
      | { type: 'showMore'; folderId: string; depth: number }
      | { type: 'moreMatches'; depth: number }
    > = [];

    const appendRevealedChildren = (
      folderId: string,
      childDepth: number
    ): void => {
      const revealed = revealedChildren.get(folderId);
      if (!revealed) {
        return;
      }
      for (const child of revealed.children) {
        // Revealed children are non-matching by definition. Render as
        // `folder` (chevron, expandable) when the API reports they have
        // their own children, OR when we've already fetched / are fetching
        // those children. Otherwise render as a `match` leaf.
        const isExpandedRevealed = expandedFolders.has(child.id);
        const childIsKnownFolder =
          (child.childCount ?? 0) > 0 ||
          revealedChildren.has(child.id) ||
          loadingFolders.has(child.id);
        if (childIsKnownFolder) {
          result.push({
            type: 'folder',
            kind: 'revealed',
            id: child.id,
            label: child.label,
            depth: childDepth,
            hasSpineChildren: false,
            repositoryCount: child.repositoryCount,
            icon: child.icon,
            isEditable: child.isEditable
          });
          if (isExpandedRevealed) {
            appendRevealedChildren(child.id, childDepth + 1);
          }
        } else {
          result.push({
            type: 'match',
            kind: 'revealed',
            id: child.id,
            label: child.label,
            depth: childDepth,
            icon: child.icon,
            repositoryCount: child.repositoryCount,
            isEditable: child.isEditable
          });
        }
      }
      // If the server has more pages, surface a "Show more" affordance
      // that triggers another fetch.
      if (revealed.hasMore) {
        result.push({
          type: 'showMore',
          folderId,
          depth: childDepth
        });
      }
    };

    // Tracks the shallowest collapsed spine-folder depth currently in effect;
    // any row deeper than this is hidden until we encounter a row at or
    // above that depth.
    let collapsedAtDepth: number | null = null;
    for (const row of basePrunedRows) {
      if (collapsedAtDepth !== null && row.depth > collapsedAtDepth) {
        continue;
      }
      if (collapsedAtDepth !== null && row.depth <= collapsedAtDepth) {
        collapsedAtDepth = null;
      }
      if (row.type === 'folder' && collapsedSpineFolders.has(row.id)) {
        result.push(row);
        collapsedAtDepth = row.depth;
        continue;
      }

      // Promote hit-leaves with known children to folder rows. The match row
      // itself becomes a folder; if the user has expanded it, append its
      // revealed children below.
      if (
        row.type === 'match' &&
        row.kind === 'hit' &&
        (hiddenSiblingCountByFolder?.get(row.id) ?? 0) > 0
      ) {
        result.push({
          type: 'folder',
          kind: 'hit',
          matchIndex: row.matchIndex,
          id: row.id,
          label: row.label,
          depth: row.depth,
          hasSpineChildren: false,
          repositoryCount: row.repositoryCount,
          icon: row.icon
        });
        if (expandedFolders.has(row.id) && revealedChildren.has(row.id)) {
          appendRevealedChildren(row.id, row.depth + 1);
        }
        continue;
      }

      // For spine folders' "+ N more siblings" hint: when the user has
      // expanded it and the first page is in, replace the hint with the
      // revealed children inline. Otherwise keep the hint visible (the
      // renderer shows "Loading…" while fetching).
      if (
        row.type === 'more' &&
        expandedFolders.has(row.folderId) &&
        revealedChildren.has(row.folderId)
      ) {
        appendRevealedChildren(row.folderId, row.depth);
      } else {
        result.push(row);
      }
    }
    if (moreMatchCount !== undefined) {
      result.push({ type: 'moreMatches', depth: 0 });
    }
    return result;
  }, [
    basePrunedRows,
    revealedChildren,
    expandedFolders,
    collapsedSpineFolders,
    loadingFolders,
    hiddenSiblingCountByFolder,
    moreMatchCount
  ]);

  const virtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => 36,
    overscan: 10
  });

  // Scroll the active match into view when it changes. Virtualized rows
  // outside the current window aren't mounted, so `scrollIntoView` on a DOM
  // query no longer works — resolve the match's index into `visibleRows`
  // (distinct from `activeMatchIndex`, which indexes into `searchHits`) and
  // ask the virtualizer to scroll to it directly. Read `visibleRows` via a
  // ref (kept current every render) rather than as a dependency, so an
  // unrelated reveal/expand doesn't re-scan the list and re-scroll to the
  // active match.
  const visibleRowsRef = useRef(visibleRows);
  visibleRowsRef.current = visibleRows;

  // Keyed on the id so appending a page doesn't scroll back up; the parent
  // remounts per term, so a repeated best match still scrolls.
  const activeMatchId = searchHits.at(activeMatchIndex)?.item.id;
  useEffect(() => {
    if (activeMatchId === undefined) {
      return;
    }
    const rowIndex = visibleRowsRef.current.findIndex(
      (row) =>
        (row.type === 'folder' || row.type === 'match') &&
        row.kind === 'hit' &&
        row.id === activeMatchId
    );
    if (rowIndex >= 0) {
      virtualizer.scrollToIndex(rowIndex, { align: 'auto' });
    }
  }, [activeMatchId, virtualizer]);

  const expandFolder = useCallback(
    (folderId: string) => {
      setExpandedFolders((prev) => {
        const next = new Set(prev);
        next.add(folderId);
        return next;
      });
      if (!revealedChildren.has(folderId) && !loadingFolders.has(folderId)) {
        onLoadChildren(folderId);
      }
    },
    [revealedChildren, loadingFolders, onLoadChildren]
  );

  const collapseFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      next.delete(folderId);
      return next;
    });
  }, []);

  const toggleFolder = useCallback(
    (folderId: string) => {
      if (expandedFolders.has(folderId)) {
        collapseFolder(folderId);
      } else {
        expandFolder(folderId);
      }
    },
    [expandedFolders, expandFolder, collapseFolder]
  );

  const toggleSpineFolder = useCallback((folderId: string) => {
    setCollapsedSpineFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  return (
    <div
      ref={containerRef}
      role="tree"
      aria-label="Search matches in tree"
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        flex: 1,
        overflowY: 'auto'
      }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative'
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const index = virtualRow.index;
          const row = visibleRows[index];
          if (!row) {
            return null;
          }
          const indent = row.depth * INDENT_PX + BASE_PL;

          if (row.type === 'moreMatches') {
            const count = moreMatchCount ?? 0;
            return (
              <HintRow
                key={`more-matches-${index}`}
                index={index}
                start={virtualRow.start}
                indent={indent}
                label={
                  isLoadingMoreMatches
                    ? 'Loading…'
                    : count > 0
                      ? // NOTE: pluralize() would give "matchs".
                        `+ ${formatNumber({ value: count })} more ${count === 1 ? 'match' : 'matches'}`
                      : '+ Show more matches'
                }
                ariaLabel="Show more matches"
                onClick={onLoadMoreMatches}
                disabled={isLoadingMoreMatches}
                measureRef={virtualizer.measureElement}
              />
            );
          }

          if (row.type === 'showMore') {
            const isLoadingMore = loadingFolders.has(row.folderId);
            const hasFailed = failedFolders.has(row.folderId);
            return (
              <HintRow
                key={`showmore-${row.folderId}-${index}`}
                index={index}
                start={virtualRow.start}
                indent={indent}
                label={
                  hasFailed
                    ? 'Failed to load — click to retry'
                    : isLoadingMore
                      ? 'Loading…'
                      : '+ Show more'
                }
                ariaLabel={hasFailed ? 'Retry loading more' : 'Show more'}
                onClick={() => onLoadMoreChildren(row.folderId)}
                disabled={isLoadingMore}
                hasFailed={hasFailed}
                measureRef={virtualizer.measureElement}
              />
            );
          }

          if (row.type === 'folder') {
            const isFolderHit = row.kind === 'hit';
            const isFolderActiveMatch =
              isFolderHit && row.matchIndex === activeMatchIndex;
            const isExpanded = row.hasSpineChildren
              ? !collapsedSpineFolders.has(row.id)
              : expandedFolders.has(row.id);
            const showRowActions = !isBucketRow(row.id);

            return (
              <RowHoverWrapper
                key={`folder-${row.id}-${index}`}
                elementId={
                  row.kind === 'hit'
                    ? matchRowElementId(row.matchIndex)
                    : rowElementId(`f-${row.id}-${index}`)
                }
                tabIndex={isFolderActiveMatch ? 0 : -1}
                ariaExpanded={isExpanded}
                ariaSelected={isFolderActiveMatch}
                onContextMenu={
                  showRowActions
                    ? (event) =>
                        onOpenRowContextMenu(event, {
                          id: row.id,
                          name: row.label,
                          isEditable: Boolean(row.isEditable)
                        })
                    : undefined
                }
                isHit={isFolderHit}
                virtualRow={virtualRow}
                measureRef={virtualizer.measureElement}
              >
                <ButtonBase
                  onClick={() => {
                    onSelect(row.id);
                    if (row.kind === 'hit') {
                      onMatchClick(row.matchIndex);
                    }
                  }}
                  sx={{
                    ...ROW_BASE_SX,
                    flexDirection: 'row',
                    pl: `${indent}px`,
                    backgroundColor: 'transparent'
                  }}
                >
                  <ModIconButton
                    size="small"
                    component="span"
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${row.label}`}
                    onClick={(e) => {
                      // Toggle without selecting the org.
                      e.stopPropagation();
                      if (row.hasSpineChildren) {
                        toggleSpineFolder(row.id);
                      } else {
                        toggleFolder(row.id);
                      }
                    }}
                    sx={{
                      mr: 1.5,
                      '&:hover': {
                        backgroundColor: semanticColors.button.secondary.bgHover
                      }
                    }}
                  >
                    {isExpanded ? (
                      <CollapseIcon size={iconSize.lg} />
                    ) : (
                      <ExpandIcon size={iconSize.lg} />
                    )}
                  </ModIconButton>
                  <Stack
                    direction="row"
                    sx={{ alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}
                  >
                    {row.icon && (
                      <row.icon
                        size={iconSize.md}
                        style={{
                          flexShrink: 0,
                          color: semanticColors.color.icon.utility
                        }}
                      />
                    )}
                    <ModTooltip title={row.label} enterDelay={500}>
                      <Typography
                        component="div"
                        sx={{
                          fontSize: typography.fontSize.sm,
                          fontWeight: typography.fontWeight.regular,
                          color: isFolderHit
                            ? semanticColors.color.text.primary
                            : semanticColors.color.text.secondary,
                          minWidth: 0,
                          flexShrink: 1
                        }}
                      >
                        <MiddleTruncatedLabel
                          label={row.label}
                          renderText={
                            isFolderHit
                              ? (text) => (
                                  <BoldedMatch text={text} term={searchTerm} />
                                )
                              : undefined
                          }
                        />
                      </Typography>
                    </ModTooltip>
                    {row.repositoryCount != null && row.repositoryCount > 0 && (
                      <ModTag
                        label={<LocaleNumber value={row.repositoryCount} />}
                        size="small"
                      />
                    )}
                    {showRowActions && <RowCopyAction label={row.label} />}
                  </Stack>
                </ButtonBase>
              </RowHoverWrapper>
            );
          }

          if (row.type === 'more') {
            const count = hiddenSiblingCountByFolder?.get(row.folderId);
            const hasFailed = failedFolders.has(row.folderId);
            if (count === 0 && !hasFailed) {
              return null;
            }
            const isLoading = loadingFolders.has(row.folderId);
            return (
              <HintRow
                key={`more-${row.folderId}-${index}`}
                index={index}
                start={virtualRow.start}
                indent={indent}
                label={
                  hasFailed
                    ? 'Failed to load — click to retry'
                    : isLoading
                      ? 'Loading…'
                      : count != null
                        ? `+ ${formatNumber({ value: count })} more ${pluralize('item', count)}`
                        : '+ Show more siblings'
                }
                ariaLabel={
                  hasFailed
                    ? 'Retry loading'
                    : count != null
                      ? `Show ${formatNumber({ value: count })} more items`
                      : 'Show more siblings'
                }
                onClick={() => expandFolder(row.folderId)}
                disabled={isLoading}
                hasFailed={hasFailed}
                measureRef={virtualizer.measureElement}
              />
            );
          }

          // Match row — either a hit leaf or a revealed (non-spine) leaf
          const isRealMatch = row.kind === 'hit';
          const isActiveMatch =
            isRealMatch && row.matchIndex === activeMatchIndex;
          const Icon = row.icon;
          const showRowActions = !isBucketRow(row.id);

          return (
            <RowHoverWrapper
              key={`item-${row.id}-${index}`}
              elementId={
                row.kind === 'hit'
                  ? matchRowElementId(row.matchIndex)
                  : rowElementId(`i-${row.id}-${index}`)
              }
              tabIndex={isActiveMatch ? 0 : -1}
              ariaSelected={isActiveMatch}
              onContextMenu={
                showRowActions
                  ? (event) =>
                      onOpenRowContextMenu(event, {
                        id: row.id,
                        name: row.label,
                        isEditable: Boolean(row.isEditable)
                      })
                  : undefined
              }
              isHit={isRealMatch}
              virtualRow={virtualRow}
              measureRef={virtualizer.measureElement}
            >
              <ButtonBase
                onClick={() => {
                  if (row.kind === 'hit') {
                    onMatchClick(row.matchIndex);
                  }
                  onSelect(row.id);
                }}
                sx={{
                  ...ROW_BASE_SX,
                  flexDirection: 'row',
                  pl: `${indent}px`,
                  backgroundColor: 'transparent'
                }}
              >
                <div
                  style={{
                    width: ICON_BUTTON_SMALL_SIZE,
                    height: ICON_BUTTON_SMALL_SIZE,
                    flexShrink: 0,
                    marginRight: spacing.spacing_1_1_2
                  }}
                />
                <Stack
                  direction="row"
                  sx={{ alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}
                >
                  {Icon && (
                    <Icon
                      size={iconSize.md}
                      style={{
                        flexShrink: 0,
                        color: semanticColors.color.icon.utility
                      }}
                    />
                  )}
                  <ModTooltip title={row.label} enterDelay={500}>
                    <Typography
                      component="div"
                      sx={{
                        fontSize: typography.fontSize.sm,
                        fontWeight: typography.fontWeight.regular,
                        color: isRealMatch
                          ? semanticColors.color.text.primary
                          : semanticColors.color.text.secondary,
                        minWidth: 0,
                        flexShrink: 1
                      }}
                    >
                      <MiddleTruncatedLabel
                        label={row.label}
                        renderText={
                          isRealMatch
                            ? (text) => (
                                <BoldedMatch text={text} term={searchTerm} />
                              )
                            : undefined
                        }
                      />
                    </Typography>
                  </ModTooltip>
                  {row.repositoryCount != null && row.repositoryCount > 0 && (
                    <ModTag
                      label={<LocaleNumber value={row.repositoryCount} />}
                      size="small"
                    />
                  )}
                  {showRowActions && <RowCopyAction label={row.label} />}
                </Stack>
              </ButtonBase>
            </RowHoverWrapper>
          );
        })}
      </div>
    </div>
  );
};
